import { Component } from '@theme/component';
import { colourChoices, colourGroup, matchingVariant, visibleColours } from '@theme/nff-colour-data';
const cache = new Map(), queue = [];
let running = 0, scheduled = false;
const observers = new Map();
const visibility = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    const component = observers.get(entry.target);
    observers.delete(entry.target);
    visibility.unobserve(entry.target);
    component?.loadColours();
  }
}, { rootMargin: '240px' }) : null;
function requestColours(parent) {
  const existing = cache.get(parent);
  if (existing && existing.expires > Date.now()) return existing.promise;
  let resolve, reject;
  const promise = new Promise((yes, no) => { resolve = yes; reject = no; });
  const record = { promise, expires: Infinity };
  cache.set(parent, record);
  queue.push({ parent, resolve, reject, record });
  if (!scheduled) {
    scheduled = true;
    queueMicrotask(() => { scheduled = false; pump(); });
  }
  return promise;
}
function pump() {
  while (running < 3 && queue.length) {
    const batch = queue.splice(0, 6);
    running++;
    fetchColours(batch.map(job => job.parent)).then(items => {
      for (const job of batch) {
        job.record.expires = Date.now() + 60000;
        job.resolve(items.filter(item => item.parent === job.parent));
      }
    }).catch(error => {
      for (const job of batch) { cache.delete(job.parent); job.reject(error); }
    }).finally(() => { running--; pump(); });
  }
}
async function fetchColours(parents) {
  const root = window.Shopify?.routes?.root || '/';
  const url = new URL(root + 'search', location.origin);
  url.searchParams.set('type', 'product');
  url.searchParams.set('view', 'nff-colours');
  url.searchParams.set('options[unavailable_products]', 'last');
  url.searchParams.set('q', parents.map(p => 'tag:"' + p.replace(/["\\]/g, '\\$&') + '"').join(' OR '));
  const products = [];
  let pages = 1;
  for (let page = 1; page <= pages; page++) {
    url.searchParams.set('page', String(page));
    const response = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Colour lookup failed');
    const data = JSON.parse((await response.text()).trim());
    if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages > 100) throw new Error('Invalid colour data');
    pages = data.pages;
    products.push(...data.products);
  }
  return products;
}
const palette = { black:'#222',white:'#fff',cream:'#f3ead9',grey:'#999',gray:'#999',
  beige:'#e3d5c3',tan:'#c08a54',brown:'#6b4526',blue:'#2f5f9e',navy:'#23304a',
  green:'#4c7a4a',red:'#c02a2a',pink:'#e0a0b0',purple:'#7a5aa0',orange:'#e08a3c',
  yellow:'#e5c451',metallic:'#b9bcc0',gold:'#d9b45a',silver:'#b9bcc0',animal:'#b79d78',multi:'#aaa' };
function fillCircle(circle, item) {
  if (item.image) {
    const image = new Image();
    image.className = 'nff-colours__image'; image.alt = ''; image.width = 120; image.height = 120;
    image.loading = 'lazy'; image.src = item.image;
    circle.append(image);
    return;
  }
  const hex = String(item.hex || '').replace(/^#/, '');
  const family = Array.isArray(item.family) ? item.family[0] : item.family;
  circle.style.backgroundColor = /^(?:[a-f0-9]{3}|[a-f0-9]{6}|[a-f0-9]{8})$/i.test(hex)
    ? '#' + hex : palette[String(family || '').toLowerCase()] || '#dcdcdc';
}
/** @extends {Component<{colours: HTMLElement, name?: HTMLElement}>} */
class NFFLinkedColours extends Component {
  requiredRefs = ['colours'];
  #version = 0;
  #target;
  #listeners;
  connectedCallback() { super.connectedCallback(); this.#prepare(); }
  updatedCallback() { super.updatedCallback(); this.#prepare(); }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#version++; this.#listeners?.abort(); this.#unobserve();
  }
  #unobserve() {
    if (this.#target) {
      visibility?.unobserve(this.#target); observers.delete(this.#target); this.#target = undefined;
    }
  }
  #prepare() {
    this.#version++; this.#listeners?.abort(); this.#unobserve();
    if (this.dataset.mode !== 'card' || !visibility) this.loadColours();
    else {
      this.#target = this.closest('product-card') || this;
      observers.set(this.#target, this); visibility.observe(this.#target);
    }
  }
  /** Load all published colours of this exact style. */
  async loadColours() {
    const version = ++this.#version;
    try {
      const data = await requestColours(this.dataset.parent);
      if (!this.isConnected || version !== this.#version) return;
      const params = new URL(location.href).searchParams;
      const card = this.dataset.mode === 'card';
      const sizes = card ? params.getAll('filter.v.option.size') : [];
      const widths = card ? [...params.getAll('filter.v.option.width'), ...params.getAll('filter.v.m.custom.width')] : [];
      const items = colourChoices(data, this.dataset.parent, this.dataset.vendor, sizes, widths);
      if (items.length < 2) { this.hidden = true; this.refs.colours.replaceChildren(); return; }
      const max = card ? Math.min(Number(this.dataset.maximum) || 5, matchMedia('(max-width:749px)').matches ? 4 : 8) : 0;
      const { shown, remaining } = visibleColours(items, this.dataset.current, max);
      const pricesVary = new Set(items.map(item => item.price)).size > 1;
      this.#listeners?.abort(); this.#listeners = new AbortController();
      const current = items.find(item => item.handle === this.dataset.current);
      const currentName = current?.colour || this.refs.name?.textContent || '';
      if (this.refs.name) this.refs.name.textContent = currentName;
      const groups = new Map();
      for (const item of shown) {
        const key = !card && this.dataset.grouped === 'true' ? colourGroup(item.phase) : 'all';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      }
      const fragment = document.createDocumentFragment();
      for (const key of ['all', 'core', 'seasonal', 'sale']) {
        const group = groups.get(key);
        if (!group) continue;
        const wrapper = document.createElement('div');
        if (groups.size > 1) {
          const title = document.createElement('p');
          title.className = 'nff-colours__heading'; title.textContent = this.dataset[key]; wrapper.append(title);
        }
        const row = document.createElement('div'); row.className = 'nff-colours__row';
        row.setAttribute('role', 'group'); row.setAttribute('aria-label', this.dataset.select.replace('__COLOUR__', ''));
        for (const item of group) {
          const url = new URL(item.url, location.origin);
          if (url.origin !== location.origin) continue;
          if (sizes.length || widths.length) {
            const variant = matchingVariant(item, sizes, widths);
            if (variant) url.searchParams.set('variant', variant.id);
          }
          const link = document.createElement('a');
          link.className = 'nff-colours__link' + (!item.available ? ' nff-colours__link--unavailable' : '');
          link.href = url.pathname + url.search;
          const label = item.colour || item.title;
          link.title = label + (!item.available ? ' · ' + this.dataset.soldOut : '');
          link.setAttribute('aria-label', this.dataset.select.replace('__COLOUR__', label) +
            (!item.available ? ' · ' + this.dataset.soldOut : ''));
          if (item.handle === this.dataset.current) link.setAttribute('aria-current', 'true');
          const circle = document.createElement('span'); circle.className = 'nff-colours__circle';
          circle.setAttribute('aria-hidden', 'true'); fillCircle(circle, item); link.append(circle);
          if (!card && pricesVary) {
            const price = document.createElement('span');
            price.className = 'nff-colours__price' + (item.on_sale ? ' nff-colours__price--sale' : '');
            price.textContent = item.price_varies ? this.dataset.from.replace('__PRICE__', item.price) : item.price;
            link.append(price);
          }
          if (this.refs.name) {
            const update = () => { this.refs.name.textContent = label; };
            const restore = () => { this.refs.name.textContent = currentName; };
            for (const event of ['pointerenter', 'focus']) link.addEventListener(event, update, {signal:this.#listeners.signal});
            for (const event of ['pointerleave', 'blur']) link.addEventListener(event, restore, {signal:this.#listeners.signal});
          }
          row.append(link);
        }
        if (remaining) {
          const more = document.createElement('a'); more.className = 'nff-colours__more';
          more.href = this.dataset.url; more.textContent = '+' + remaining;
          more.setAttribute('aria-label', this.dataset.more.replace('__COUNT__', String(items.length))); row.append(more);
        }
        wrapper.append(row); fragment.append(wrapper);
      }
      this.refs.colours.replaceChildren(fragment); this.hidden = false;
    } catch {
      // Existing product links and purchase forms remain usable when colour data cannot load.
      if (version === this.#version) this.hidden = true;
    }
  }
}
if (!customElements.get('nff-linked-colours')) customElements.define('nff-linked-colours', NFFLinkedColours);
